import type { Frequency, PackRelease, ReleaseDate } from '../components/PackSizesPanel';
import type { Product } from '../components/ProductSourceModal';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The first release on a pack defaults to 1 March
const DEFAULT_FIRST_RELEASE: ReleaseDate = {
  month: 3,
  day: 1
};

export interface ReleaseContentLine {
  product: Product;
  qty: number;
}

// Releases in calendar order through the year
export function sortReleases(releases: PackRelease[]): PackRelease[] {
  return [...releases].sort((a, b) => a.month - b.month || a.day - b.day);
}

export function getReleaseDates(releases: PackRelease[]): ReleaseDate[] {
  return releases.map(({
    month,
    day
  }) => ({
    month,
    day
  }));
}

// "1 March release"
export function formatReleaseTitle(release: ReleaseDate): string {
  return `${release.day} ${MONTHS_LONG[release.month - 1]} release`;
}

// "Releases: 1 Mar (Autumn/Winter), 1 Sep (Spring/Summer)"
export function formatReleasesSummary(releases: PackRelease[]): string {
  if (releases.length === 0) return 'No releases';
  const parts = sortReleases(releases).map(release => {
    const name = release.name.trim();
    return `${release.day} ${MONTHS_SHORT[release.month - 1]}${name ? ` (${name})` : ''}`;
  });
  return `Releases: ${parts.join(', ')}`;
}

// The release's products (limited to the pack's product list), bottle count and retail value
export function getReleaseContents(release: PackRelease, packProducts: Product[]) {
  const lines: ReleaseContentLine[] = [];
  Object.keys(release.products).forEach(id => {
    const qty = release.products[id];
    const product = packProducts.find(p => p.id === id);
    if (product && qty > 0) lines.push({
      product,
      qty
    });
  });
  const bottles = lines.reduce((sum, line) => sum + line.qty, 0);
  const retail = lines.reduce((sum, line) => sum + line.product.price * line.qty, 0);
  return {
    lines,
    bottles,
    retail
  };
}

// "3 products, 12 bottles"
export function formatContentsSummary(productCount: number, bottles: number): string {
  if (productCount === 0) return 'No products yet';
  return `${productCount} ${productCount === 1 ? 'product' : 'products'}, ${bottles} ${bottles === 1 ? 'bottle' : 'bottles'}`;
}

// What members pay for this release, or null when it can't be worked out yet
export function getReleasePrice(release: PackRelease, retail: number): number | null {
  if (release.discountType === 'flat') {
    const flat = parseFloat(release.flatPrice);
    return Number.isFinite(flat) ? flat : null;
  }
  if (retail <= 0) return null;
  const percent = parseFloat(release.discount);
  return Number.isFinite(percent) ? retail * (1 - percent / 100) : retail;
}

export function formatMoney(amount: number): string {
  return `$${amount.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

// Drops products that are no longer in the pack's product list
export function keepPackProducts(products: Record<string, number>, packProductIds: string[]): Record<string, number> {
  return Object.keys(products).reduce<Record<string, number>>((kept, id) => {
    if (packProductIds.includes(id)) kept[id] = products[id];
    return kept;
  }, {});
}

// New release dated the month after the latest one (or 1 March for the first),
// optionally seeded from an existing frequency's settings
export function createRelease(existing: PackRelease[], seed?: Frequency): PackRelease {
  const sorted = sortReleases(existing);
  const latest = sorted[sorted.length - 1];
  const date = latest ? {
    month: latest.month % 12 + 1,
    day: latest.day
  } : DEFAULT_FIRST_RELEASE;
  const seededDiscount = seed?.discount ? parseFloat(seed.discount) : NaN;
  return {
    id: `release-${Date.now()}`,
    ...date,
    name: '',
    discountType: 'percentage',
    discount: Number.isFinite(seededDiscount) ? String(seededDiscount) : '',
    flatPrice: '',
    loyaltyTier: seed?.loyaltyTier ?? 'None',
    adHocDiscount: seed?.adHocDiscount ?? '0',
    pausedDiscount: seed?.pausedDiscount ?? '0',
    products: {},
    isRowExpanded: true
  };
}
